import type { IconName } from "./icons.js";

export const TAB_GROUPS = [
  { label: "", tabs: ["chat", "capabilities", "cron", "files"] },
] as const;

export type Tab =
  | "chat"
  | "capabilities"
  | "cron"
  | "files";

export type ExploreSubTab = "mcp" | "usecases";

const TAB_PATHS: Record<Tab, string> = {
  chat: "/chat",
  capabilities: "/capabilities",
  cron: "/cron",
  files: "/files",
};

const PATH_TO_TAB = new Map(
  Object.entries(TAB_PATHS).map(([tab, path]) => [path, tab as Tab]),
);

export function normalizeBasePath(basePath: string): string {
  if (!basePath) return "";
  let base = basePath.trim();
  if (!base.startsWith("/")) base = `/${base}`;
  if (base === "/") return "";
  if (base.endsWith("/")) base = base.slice(0, -1);
  return base;
}

export function normalizePath(path: string): string {
  if (!path) return "/";
  let normalized = path.trim();
  if (!normalized.startsWith("/")) normalized = `/${normalized}`;
  if (normalized.length > 1 && normalized.endsWith("/")) {
    normalized = normalized.slice(0, -1);
  }
  return normalized;
}

export function pathForTab(tab: Tab, basePath = ""): string {
  const base = normalizeBasePath(basePath);
  const path = TAB_PATHS[tab];
  return base ? `${base}${path}` : path;
}

export function tabFromPath(pathname: string, basePath = ""): Tab | null {
  const base = normalizeBasePath(basePath);
  let path = pathname || "/";
  if (base) {
    if (path === base) {
      path = "/";
    } else if (path.startsWith(`${base}/`)) {
      path = path.slice(base.length);
    }
  }
  let normalized = normalizePath(path).toLowerCase();
  if (normalized.endsWith("/index.html")) normalized = "/";
  if (normalized === "/") return "chat";
  return PATH_TO_TAB.get(normalized) ?? null;
}

export function inferBasePathFromPathname(pathname: string): string {
  let normalized = normalizePath(pathname);
  if (normalized.endsWith("/index.html")) {
    normalized = normalizePath(normalized.slice(0, -"/index.html".length));
  }
  if (normalized === "/") return "";
  const segments = normalized.split("/").filter(Boolean);
  if (segments.length === 0) return "";
  for (let i = 0; i < segments.length; i++) {
    const candidate = `/${segments.slice(i).join("/")}`.toLowerCase();
    if (PATH_TO_TAB.has(candidate)) {
      const prefix = segments.slice(0, i);
      return prefix.length ? `/${prefix.join("/")}` : "";
    }
  }
  return `/${segments.join("/")}`;
}

export function iconForTab(tab: Tab): IconName {
  switch (tab) {
    case "chat":
      return "messageSquare";
    case "capabilities":
      return "compass";
    case "cron":
      return "clock";
    case "files":
      return "folder";
    default:
      return "folder";
  }
}

export function titleForTab(tab: Tab) {
  switch (tab) {
    case "chat":
      return "Chat";
    case "capabilities":
      return "Explore";
    case "cron":
      return "Routines";
    case "files":
      return "Files";
    default:
      return "Sontara";
  }
}

export function subtitleForTab(tab: Tab) {
  switch (tab) {
    case "chat":
      return "Talk to your agent.";
    case "capabilities":
      return "Setup integrations and explore use cases.";
    case "cron":
      return "Scheduled automations.";
    case "files":
      return "Agent-created files.";
    default:
      return "";
  }
}
